import React, { useCallback, useEffect, useState } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import { shallow } from 'zustand/shallow'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'
import { useApiErrorMessage } from '../../i18n/useApiErrorMessage'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Grid from '@mui/material/Grid'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid'

import { MaterialIcons } from '../../icons/Material/MaterialIcons'

import Filtros from '../Modals/Filtros/Filtros'
import Layout from '../Layout/Layout'
import DataTable from '../table/DataTable'

import {
  getCompanyPeriods,
  getCompanyPeriodsTypes,
} from '../../services/CompaniesService'

import { useEmpresaStore } from '../../store/empresaStore'

import { CompanyPeriodsType, TipoPeriodoRowsType } from '../../types'

const tableFieldsName = [
  'duration',
  'start_date',
  'finish_date',
  'day_limit',
  'period_type_id',
  'period_state',
]

const Periodos = () => {
  const { t } = useTranslation('periodos')
  const getApiErrorMessage = useApiErrorMessage()

  const periodsStatuses = [
    { id: '1', value: 0, label: t('status.active') },
    { id: '2', value: 1, label: t('status.inactive') },
  ]

  const tableColumnsName = [
    t('list.columns.duration'),
    t('list.columns.startDate'),
    t('list.columns.endDate'),
    t('list.columns.dayLimit'),
    t('list.columns.type'),
    t('list.columns.status'),
  ]

  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [periods, setPeriods] = useState<CompanyPeriodsType[]>([])
  const [originalPeriods, setOriginalPeriods] = useState<CompanyPeriodsType[]>([])
  const [companyPeriodsType, setCompanyPeriodsType] = useState<TipoPeriodoRowsType[]>()
  const [fetchErrorMessage, setFetchErrorMessage] = useState<string>('')
  const [filterValues, setFilterValues] = useState<Record<string, string>>({})
  const [openFiltersModal, setOpenFilterModal] = useState<boolean>(false)
  const [isFiltering, setIsFiltering] = useState<boolean>(false)

  const { getAccessTokenSilently } = useAuth0()

  const empresaId = useEmpresaStore(state => state.empresaId, shallow)

  const handleOpenModal = (open: boolean) => {
    setOpenFilterModal(open)
  }

  const handleClearFilterButton = () => {
    setFilterValues({})
    setPeriods(originalPeriods)
    setIsFiltering(false)
    handleOpenModal(false)
  }

  const handleSetFilter = (newFilter: any) => {
    setPeriods(newFilter)
  }

  const handleFilterWithButton = (value: string, name: string) => {
    setFilterValues({ ...filterValues, [name]: value })
  }

  const handleOnFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target
    setFilterValues({ ...filterValues, [name]: value })
  }

  const handleOnFilterDateChange = (date: string, name: string) => {
    if (date.toLowerCase() === 'invalid date') {
      setFilterValues({ ...filterValues, [name]: '' })
    } else {
      setFilterValues({ ...filterValues, [name]: date })
    }
  }

  const applyFilters = () => {
    const dateFields = ['start_date', 'finish_date', 'day_limit']

    if (Object.keys(filterValues).length > 0) {
      const values = Object.values(filterValues)
      if (values.every(value => !value)) {
        setIsFiltering(false)
      } else {
        const filteredRows: any = originalPeriods.filter((row: any) => {
          for (const key in filterValues) {
            if (dateFields.indexOf(key) !== -1) {
              if (dayjs(row[key]).format('DD/MM/YYYY') === filterValues[key]) {
                continue
              } else {
                return false
              }
            }
            if (filterValues[key]) {
              if (
                row[key]
                  ?.toString()
                  .toLowerCase()
                  .includes(filterValues[key].toString().toLowerCase())
              ) {
                continue
              } else {
                return false
              }
            }
            if (filterValues[key]) {
              if (
                row[key]
                  ?.toString()
                  .toLowerCase()
                  .includes(filterValues[key].toLowerCase())
              ) {
                continue
              } else {
                return false
              }
            }
          }
          return true
        })
        setIsFiltering(true)
        handleSetFilter(filteredRows)
      }
    } else {
      setIsFiltering(false)
    }
    handleOpenModal(false)
  }

  const getPeriods = useCallback(async () => {
    setIsLoading(true)
    const token = await getAccessTokenSilently()
    getCompanyPeriods(token, empresaId)
      .then(result => {
        getCompanyPeriodsTypes(token, empresaId)
          .then(result2 => {
            setFetchErrorMessage('')
            const activePeriodTypeIds = new Set(result2.data.map((pt: any) => pt.id))
            const filteredPeriods = result.data.filter((p: any) =>
              activePeriodTypeIds.has(p.period_type_id),
            )
            setPeriods(filteredPeriods)
            setOriginalPeriods(filteredPeriods)
            setCompanyPeriodsType(result2.data)
            setIsLoading(false)
          })
          .catch(e => {
            console.error(e.response.data.error)
            setFetchErrorMessage(getApiErrorMessage(e))
            setIsLoading(false)
          })
      })
      .catch(e => {
        console.error(e.response.data.error)
        setFetchErrorMessage(getApiErrorMessage(e))
        setIsLoading(false)
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empresaId])

  useEffect(() => {
    getPeriods()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  //1: success, 2:pending, 3:cancel
  const getCompanyPeriod = (params: GridRenderCellParams) => {
    const periodType = companyPeriodsType?.find(
      cmpyPT => cmpyPT.company_id === empresaId && cmpyPT.id === params.value,
    )
    return periodType?.name || t('list.notAssigned')
  }

  //1: success, 2:pending, 3:cancel
  const getAction = (params: GridRenderCellParams) => {
    return (
      <Box
        sx={{
          display: 'flex',
          placeItems: 'center',
          justifyContent: 'flex-end',
          width: '100%',
        }}
      >
        <MaterialIcons.VisibilityOutlined />
      </Box>
    )
  }

  //0:active, 1:inactive
  const getStatus = (params: GridRenderCellParams) => {
    if (params.value === 0) {
      return <Typography marginLeft="8px">{t('status.active')}</Typography>
    }

    if (params.value === 1) {
      return <Typography marginLeft="8px">{t('status.inactive')}</Typography>
    }
  }

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: t('list.columns.name'),
      headerClassName: 'theme--header',
      flex: 1,
    },
    {
      field: 'duration',
      headerName: t('list.columns.duration'),
      headerClassName: 'theme--header',
      flex: 1,
    },
    {
      field: 'start_date',
      headerName: t('list.columns.startDate'),
      type: 'dateTime',
      valueFormatter: ({ value }) =>
        value ? dayjs(value).add(1, 'day').format('DD/MM/YYYY') : '',
      headerClassName: 'theme--header',
      flex: 1,
    },
    {
      field: 'finish_date',
      headerName: t('list.columns.endDate'),
      type: 'dateTime',
      // valueGetter: ({ value }) => value && dayjs(value).format("DD/MM/YYYY"),
      valueFormatter: ({ value }) => value && dayjs(value).format('DD/MM/YYYY'),
      headerClassName: 'theme--header',
      flex: 1,
    },
    {
      field: 'day_limit',
      headerName: t('list.columns.dayLimit'),
      type: 'dateTime',
      // valueGetter: ({ value }) => value && dayjs(value).format("DD/MM/YYYY"),
      valueFormatter: ({ value }) => value && dayjs(value).format('DD/MM/YYYY'),
      headerClassName: 'theme--header',
      flex: 1,
    },
    {
      field: 'period_type_id',
      headerName: t('list.columns.type'),
      sortable: false,
      headerClassName: 'theme--header',
      align: 'center',
      renderCell: getCompanyPeriod,
      flex: 1,
    },
    {
      field: 'period_state',
      headerName: t('list.columns.status'),
      type: 'number',
      headerClassName: 'theme--header',
      renderCell: getStatus,
      flex: 1,
      headerAlign: 'left',
      align: 'center',
    },
    {
      field: 'action',
      headerName: t('list.columns.action'),
      type: 'number',
      headerClassName: 'theme--header',
      renderCell: getAction,
      flex: 1,
    },
  ]

  return (
    <Layout icon="CalendarTodayIcon" title={t('list.breadcrumb')}>
      <Box
        sx={{
          height: '100%',
          '& .theme--header': {
            backgroundColor: '#EEF1FC',
          },
        }}
      >
        <Grid container sx={{ py: '24px', placeItems: 'center' }}>
          <Grid item xs>
            <Grid container display="flex" justifyContent="flex-end">
              <Stack direction="row" spacing={2}>
                <Button
                  variant="contained"
                  startIcon={<MaterialIcons.FilterAltIcon />}
                  onClick={() => setOpenFilterModal(true)}
                  sx={{
                    backgroundColor: isFiltering ? '#182F84' : 'white',
                    color: isFiltering ? 'white' : 'black',
                    '&:hover': { color: 'white' },
                  }}
                >
                  {t('list.filterButton')}
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </Grid>
        <DataTable
          columns={columns}
          rows={periods}
          noFilterRowsMessage={t('list.noFilterRowsMessage')}
          noRowsMessage={fetchErrorMessage || t('list.noRowsMessage')}
          loading={isLoading}
          navigateRowLink="/periodos/periodo"
        />

        {openFiltersModal && (
          <Filtros
            filterValues={filterValues}
            openModal={openFiltersModal}
            handleOpenModal={handleOpenModal}
            handleFilterButton={() => applyFilters()}
            handleClearFilterButton={() => handleClearFilterButton()}
            handleFilters={handleOnFilterChange}
            handleFilterDate={handleOnFilterDateChange}
            handleFilterWithButton={handleFilterWithButton}
            tableColumnsName={tableColumnsName}
            tableFieldsName={tableFieldsName}
            periodsStatuses={periodsStatuses}
            periodsTypes={companyPeriodsType || []}
            filtersTo="Periodos"
          />
        )}
      </Box>
    </Layout>
  )
}

export default Periodos
